import { FC, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router';
import API from 'core/Api';
import { useActiveClientContext } from 'store/active-client';
import { buildClientUrl, buildClientEntity } from 'utils/helpers/client';
import MainLayout from 'components/main-layout/MainLayout';
import { ISelectOptionProps } from 'components/form/types';
import IField from 'components/fields/types';
import { sortAZ } from 'utils/helpers/sortAZ';
import TokensForm from './TokensForm';
import { IToken } from '../types';

interface IProps {
  elementsEntityName: string;
  elementsEndpoint: string;
  fieldsEntityName: string;
  fieldsEndpoint: string;
  tokenIdKey: string;
  fieldIdKey: string;
  elementFilter: string;
}

const ManageTokens: FC<IProps> = ({
  elementsEntityName,
  elementsEndpoint,
  fieldsEntityName,
  fieldsEndpoint,
  tokenIdKey,
  fieldIdKey,
  elementFilter,
}) => {
  const { id, segmentId } = useParams<{ id: string; segmentId: string }>();
  const { client } = useActiveClientContext();
  const location = useLocation<{ isEdit?: boolean; elementName?: string }>();
  const { state } = location;

  const history = useHistory();

  useEffect(() => {
    /**
     * If the user has reloaded the page and location state is missing
     * send him back to editing the segment element with `id`
     */
    if (!state) history.replace(buildClientUrl(`/segments/${segmentId}/elements/edit/${id}`, client));
  }, []);

  const {
    isLoading: isLoadingTokens,
    error: errorTokens,
    data: tokens,
  } = useQuery<IToken[], Error>(
    [buildClientEntity(elementsEntityName, client), { [elementFilter]: id }],
    (): Promise<IToken[]> => {
      return API.get(`${elementsEndpoint}?${elementFilter}=${id}`).then(res => res.data?.items);
    }
  );

  const {
    isLoading: isLoadingFields,
    error: errorFields,
    data: fields,
  } = useQuery<ISelectOptionProps[], Error>(
    buildClientEntity(fieldsEntityName, client),
    (): Promise<ISelectOptionProps[]> => {
      return API.get(`${fieldsEndpoint}?is_active=true`).then(res =>
        res.data?.items?.map((field: IField) => ({
          label: field.name,
          value: field.id,
        }))
      );
    }
  );

  return (
    <MainLayout
      title="Управление на токени на елемент"
      isLoading={isLoadingTokens || isLoadingFields}
      error={errorTokens || errorFields}
    >
      {tokens && fields && (
        <TokensForm
          entityName={elementsEntityName}
          entityEndpoint={elementsEndpoint}
          tokens={tokens}
          fields={sortAZ(fields, 'label')}
          isEditMode={state?.isEdit}
          elementName={state?.elementName}
          tokenIdKey={tokenIdKey}
          fieldIdKey={fieldIdKey}
          elementFilter={elementFilter}
        />
      )}
    </MainLayout>
  );
};

export default ManageTokens;
